/**
 * Inventory export — PDF (print-friendly window) and CSV (downloadable file).
 *
 * Two detail levels:
 *   compact  — 5 essential columns. Easy to scan, fits portrait pages.
 *   detailed — all relevant columns. PDF auto-switches to landscape.
 *
 * Caller pre-filters the rows so the report matches the on-screen table 1:1
 * and passes a human-readable filterSummary that appears in the report header.
 */
import { fmt$, fmtN, fmtDate } from "../utils/format";
import { toast, escapeHtml } from "../lib/dom";
import { openModal, closeModal } from "./modal";
import { cashOnHand, ownerLoanBalance } from "../lib/derived";
import { DB } from "../lib/db";

/* ---------- Types ---------- */

export type ReportFormat = "pdf" | "csv";
export type ReportDetail = "compact" | "detailed";

export interface ReportColumn {
  label: string;
  get: (row: any) => string;
  align?: "left" | "right";
  /** Optional column footer string (e.g. totals). PDF only. */
  footer?: string;
  /** PDF: render the value as an <img src="..."> instead of escaped text.
   *  CSV: emit the URL as plain text. Column auto-sizes narrow for thumbs. */
  pdfImage?: boolean;
}

export interface ReportOptions {
  title: string;
  /** Used for CSV download filename and PDF window title. */
  fileSlug: string;
  /** Short human-readable description of what filters were applied. */
  filterSummary: string;
  /** Already-filtered rows to render. */
  rows: any[];
  columns: ReportColumn[];
  /** Optional summary stats shown above the PDF table. */
  stats?: Array<{ label: string; value: string }>;
  /** Page orientation when format is PDF. Defaults to portrait. */
  orientation?: "portrait" | "landscape";
}

/* ---------- Local helpers ---------- */

function esc(s: any): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Escape a single CSV field per RFC 4180. */
function csvCell(v: any): string {
  const s = String(v ?? "");
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function downloadFile(filename: string, mimeType: string, content: string): void {
  const blob = new Blob([content], { type: mimeType + ";charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* ---------- PDF (print-window) renderer ---------- */

function renderPdfReport(opts: ReportOptions): void {
  const win = window.open("", "_blank", "width=1100,height=1100");
  if (!win) {
    toast("Pop-up blocked — allow pop-ups for this site to use Export.", "err");
    return;
  }
  const generated = new Date().toLocaleString();
  const orientation = opts.orientation || "portrait";

  const colHeaders = opts.columns.map(c =>
    `<th style="text-align:${c.align === "right" ? "right" : "left"}">${esc(c.label)}</th>`
  ).join("");
  const colFooters = opts.columns.some(c => c.footer)
    ? `<tr>${opts.columns.map(c =>
        `<td style="text-align:${c.align === "right" ? "right" : "left"}"><b>${esc(c.footer || "")}</b></td>`
      ).join("")}</tr>`
    : "";
  const bodyRows = opts.rows.length
    ? opts.rows.map(r =>
        `<tr>${opts.columns.map(c => {
          const v = c.get(r);
          if (c.pdfImage) {
            const cell = v && v.trim()
              ? `<img src="${esc(v)}" loading="lazy" style="height:48px;width:auto;max-width:36px;object-fit:contain;display:block;margin:0 auto" alt="">`
              : "";
            return `<td style="text-align:center;width:42px">${cell}</td>`;
          }
          return `<td style="text-align:${c.align === "right" ? "right" : "left"}${c.align === "right" ? ";font-variant-numeric:tabular-nums" : ""}">${esc(v)}</td>`;
        }).join("")}</tr>`
      ).join("")
    : `<tr><td colspan="${opts.columns.length}" style="text-align:center;color:#94a3b8;padding:24px 8px">No rows match the current filters.</td></tr>`;

  const statsHtml = (opts.stats || []).map(s =>
    `<div class="stat"><div class="stat-label">${esc(s.label)}</div><div class="stat-value">${esc(s.value)}</div></div>`
  ).join("");

  const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<title>${esc(opts.title)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; margin: 0; padding: 18px 24px; color: #0f172a; background: #ffffff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  h1 { font-size: 18pt; margin: 0 0 2px; color: #0f172a; }
  .meta { font-size: 9pt; color: #64748b; margin-bottom: 14px; }
  .stats { display: flex; gap: 18px; margin: 0 0 14px; flex-wrap: wrap; padding: 10px 12px; background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 6px; }
  .stat-label { font-size: 8pt; color: #64748b; text-transform: uppercase; letter-spacing: 0.04em; }
  .stat-value { font-size: 12pt; font-weight: 600; font-variant-numeric: tabular-nums; }
  table { width: 100%; border-collapse: collapse; font-size: ${orientation === "landscape" ? "8pt" : "9pt"}; }
  thead th { background: #f1f5f9; border-bottom: 2px solid #64748b; padding: 5px 7px; text-align: left; font-weight: 600; color: #0f172a; }
  thead { display: table-header-group; }
  tfoot { display: table-row-group; }
  tbody td { border-bottom: 1px solid #e2e8f0; padding: 3px 7px; vertical-align: top; }
  tbody tr { page-break-inside: avoid; }
  tbody tr:nth-child(even) td { background: #fafbfc; }
  tfoot td { border-top: 2px solid #64748b; padding: 6px 7px; background: #f1f5f9; }
  .toolbar { position: fixed; top: 8px; right: 8px; display: flex; gap: 8px; }
  .toolbar button { padding: 6px 14px; font-size: 10pt; border: 1px solid #cbd5e1; background: #ffffff; border-radius: 4px; cursor: pointer; }
  .toolbar button:hover { background: #f1f5f9; }
  @media print {
    .toolbar { display: none !important; }
    @page { size: letter ${orientation}; margin: 0.4in; }
    body { padding: 0; }
  }
</style>
</head>
<body>
  <div class="toolbar">
    <button onclick="window.print()">🖨 Print / Save as PDF</button>
    <button onclick="window.close()">Close</button>
  </div>
  <h1>${esc(opts.title)}</h1>
  <div class="meta">Generated ${esc(generated)} &middot; ${esc(opts.filterSummary)}</div>
  ${statsHtml ? `<div class="stats">${statsHtml}</div>` : ""}
  <table>
    <thead><tr>${colHeaders}</tr></thead>
    <tbody>${bodyRows}</tbody>
    ${colFooters ? `<tfoot>${colFooters}</tfoot>` : ""}
  </table>
  <script>
    window.addEventListener("load", () => { setTimeout(() => window.print(), 200); });
  </script>
</body>
</html>`;

  win.document.open();
  win.document.write(html);
  win.document.close();
}

/* ---------- CSV renderer ---------- */

function renderCsvReport(opts: ReportOptions): void {
  const stamp = new Date().toISOString().slice(0, 10);
  const lines: string[] = [];
  // Header banner as comment rows (Excel happily ignores blanks/extra cells).
  lines.push(csvCell(opts.title));
  lines.push(csvCell(`Generated ${new Date().toLocaleString()} - ${opts.filterSummary}`));
  if (opts.stats && opts.stats.length) {
    lines.push(opts.stats.map(s => csvCell(`${s.label}: ${s.value}`)).join(","));
  }
  lines.push("");  // blank line before the data table
  // Column header
  lines.push(opts.columns.map(c => csvCell(c.label)).join(","));
  // Data rows
  opts.rows.forEach(r => {
    lines.push(opts.columns.map(c => csvCell(c.get(r))).join(","));
  });
  // Footer row if any column declared a footer.
  if (opts.columns.some(c => c.footer)) {
    lines.push(opts.columns.map(c => csvCell(c.footer || "")).join(","));
  }
  downloadFile(`${opts.fileSlug}-${stamp}.csv`, "text/csv", lines.join("\r\n"));
  toast(`CSV downloaded · ${opts.rows.length} row(s)`);
}

/* ---------- Public dispatcher ---------- */

export function runReport(format: ReportFormat, opts: ReportOptions): void {
  if (format === "csv") renderCsvReport(opts);
  else                  renderPdfReport(opts);
}

/* ---------- Column sets ---------- */

function cardsCompactColumns(totalQty: number, totalFMV: number): ReportColumn[] {
  return [
    { label: "Lot ID",      get: r => r["Card Lot ID"] || "" },
    { label: "Card Name",   get: r => r["Card Name"] || "" },
    { label: "Set",         get: r => r["Set"] || "" },
    { label: "Cond/Grade",  get: r => r["Condition/Grade"] || "" },
    { label: "Qty", align: "right", get: r => fmtN(r["Qty"]), footer: fmtN(totalQty) },
    { label: "FMV", align: "right", get: r => fmt$(r["Current FMV"]), footer: fmt$(totalFMV) },
  ];
}

function cardsDetailedColumns(totalQty: number, totalBasis: number, totalFMV: number): ReportColumn[] {
  return [
    { label: "",            get: r => r["Image URL"] || "", pdfImage: true },
    { label: "Lot ID",      get: r => r["Card Lot ID"] || "" },
    { label: "Card Name",   get: r => r["Card Name"] || "" },
    { label: "Game",        get: r => r["Game"] || "" },
    { label: "Set",         get: r => r["Set"] || "" },
    { label: "#",           get: r => r["Card #"] || "" },
    { label: "Cond/Grade",  get: r => r["Condition/Grade"] || "" },
    { label: "Class",       get: r => r["Inventory Class"] || "" },
    { label: "Status",      get: r => r["Status"] || "" },
    { label: "Qty",         align: "right", get: r => fmtN(r["Qty"]), footer: fmtN(totalQty) },
    { label: "Unit Basis",  align: "right", get: r => fmt$(r["Unit Basis"]) },
    { label: "Total Basis", align: "right", get: r => fmt$(r["Total Basis"]), footer: fmt$(totalBasis) },
    { label: "FMV",         align: "right", get: r => fmt$(r["Current FMV"]), footer: fmt$(totalFMV) },
    { label: "G/L",         align: "right", get: r => fmt$((Number(r["Current FMV"]) || 0) - (Number(r["Total Basis"]) || 0)) },
    { label: "Source",      get: r => r["Source ID"] || "" },
    { label: "Acq Date",    get: r => fmtDate(r["Acq Date"]) || "" },
    { label: "Acq Type",    get: r => r["Acq Type"] || "" },
  ];
}

function sealedCompactColumns(totalQty: number, totalFMV: number): ReportColumn[] {
  return [
    { label: "Lot ID",  get: r => r["Lot ID"] || "" },
    { label: "Product", get: r => r["Product"] || "" },
    { label: "Set",     get: r => r["Set"] || "" },
    { label: "Type",    get: r => r["Product Type"] || "" },
    { label: "Qty", align: "right", get: r => fmtN(r["Qty"]), footer: fmtN(totalQty) },
    { label: "FMV", align: "right", get: r => fmt$(r["Current FMV"]), footer: fmt$(totalFMV) },
  ];
}

function sealedDetailedColumns(totalQty: number, totalBasis: number, totalFMV: number): ReportColumn[] {
  return [
    { label: "",            get: r => r["Image URL"] || "", pdfImage: true },
    { label: "Lot ID",      get: r => r["Lot ID"] || "" },
    { label: "Product",     get: r => r["Product"] || "" },
    { label: "Game",        get: r => r["Game"] || "" },
    { label: "Set",         get: r => r["Set"] || "" },
    { label: "Type",        get: r => r["Product Type"] || "" },
    { label: "Qty",         align: "right", get: r => fmtN(r["Qty"]), footer: fmtN(totalQty) },
    { label: "Unit Basis",  align: "right", get: r => fmt$(r["Unit Basis"]) },
    { label: "Total Basis", align: "right", get: r => fmt$(r["Total Basis"]), footer: fmt$(totalBasis) },
    { label: "FMV",         align: "right", get: r => fmt$(r["Current FMV"]), footer: fmt$(totalFMV) },
    { label: "G/L",         align: "right", get: r => fmt$((Number(r["Current FMV"]) || 0) - (Number(r["Total Basis"]) || 0)) },
    { label: "Source",      get: r => r["Source ID"] || "" },
    { label: "Acq Date",    get: r => fmtDate(r["Acq Date"]) || "" },
    { label: "Acq Type",    get: r => r["Acq Type"] || "" },
  ];
}

/* ---------- Public entry points (called by views/cards.ts and views/sealed.ts) ---------- */

export interface CardsFilterSnapshot { search: string; game: string; status: string; inventoryClass: string }
export interface SealedFilterSnapshot { search: string; game: string; productType: string }

export function exportCardsReport(format: ReportFormat, detail: ReportDetail, rows: any[], filters: CardsFilterSnapshot): void {
  const totalQty   = rows.reduce((s, r) => s + (Number(r["Qty"]) || 0), 0);
  const totalBasis = rows.reduce((s, r) => s + (Number(r["Total Basis"]) || 0), 0);
  const totalFMV   = rows.reduce((s, r) => s + (Number(r["Current FMV"]) || 0), 0);

  const parts: string[] = [];
  if (filters.search)          parts.push(`Search: "${filters.search}"`);
  if (filters.game)            parts.push(`Game: ${filters.game}`);
  if (filters.status)          parts.push(`Status: ${filters.status}`);
  if (filters.inventoryClass)  parts.push(`Class: ${filters.inventoryClass}`);
  const filterSummary = parts.length ? `Filters — ${parts.join(", ")}` : "All cards (no filters applied)";

  const columns = detail === "detailed"
    ? cardsDetailedColumns(totalQty, totalBasis, totalFMV)
    : cardsCompactColumns(totalQty, totalFMV);

  runReport(format, {
    title: `Cards Inventory${detail === "detailed" ? " (Detailed)" : ""}`,
    fileSlug: `cards-inventory-${detail}`,
    filterSummary,
    rows,
    columns,
    stats: [
      { label: "Lots", value: fmtN(rows.length) },
      { label: "Cards", value: fmtN(totalQty) },
      { label: "Total FMV", value: fmt$(totalFMV) },
    ],
    orientation: detail === "detailed" ? "landscape" : "portrait",
  });
}

export function exportSealedReport(format: ReportFormat, detail: ReportDetail, rows: any[], filters: SealedFilterSnapshot): void {
  const totalQty   = rows.reduce((s, r) => s + (Number(r["Qty"]) || 0), 0);
  const totalBasis = rows.reduce((s, r) => s + (Number(r["Total Basis"]) || 0), 0);
  const totalFMV   = rows.reduce((s, r) => s + (Number(r["Current FMV"]) || 0), 0);

  const parts: string[] = [];
  if (filters.search)         parts.push(`Search: "${filters.search}"`);
  if (filters.game)           parts.push(`Game: ${filters.game}`);
  if (filters.productType)    parts.push(`Type: ${filters.productType}`);
  const filterSummary = parts.length ? `Filters — ${parts.join(", ")}` : "All sealed product (no filters applied)";

  const columns = detail === "detailed"
    ? sealedDetailedColumns(totalQty, totalBasis, totalFMV)
    : sealedCompactColumns(totalQty, totalFMV);

  runReport(format, {
    title: `Sealed Inventory${detail === "detailed" ? " (Detailed)" : ""}`,
    fileSlug: `sealed-inventory-${detail}`,
    filterSummary,
    rows,
    columns,
    stats: [
      { label: "Lots", value: fmtN(rows.length) },
      { label: "Units", value: fmtN(totalQty) },
      { label: "Total FMV", value: fmt$(totalFMV) },
    ],
    orientation: detail === "detailed" ? "landscape" : "portrait",
  });
}

/* ---------- Sales & Purchases exports ---------- */

export interface SalesFilterSnapshot { search: string; platform: string; from: string; to: string }
export interface PurchasesFilterSnapshot { search: string; vendor: string; from: string; to: string }

function summarizeSalesFilters(f: SalesFilterSnapshot): string {
  const parts: string[] = [];
  if (f.search)   parts.push(`Search: "${f.search}"`);
  if (f.platform) parts.push(`Platform: ${f.platform}`);
  if (f.from)     parts.push(`From ${f.from}`);
  if (f.to)       parts.push(`To ${f.to}`);
  return parts.length ? `Filters — ${parts.join(", ")}` : "All sales (no filters applied)";
}

function summarizePurchasesFilters(f: PurchasesFilterSnapshot): string {
  const parts: string[] = [];
  if (f.search) parts.push(`Search: "${f.search}"`);
  if (f.vendor) parts.push(`Vendor: ${f.vendor}`);
  if (f.from)   parts.push(`From ${f.from}`);
  if (f.to)     parts.push(`To ${f.to}`);
  return parts.length ? `Filters — ${parts.join(", ")}` : "All purchases (no filters applied)";
}

export function exportSalesReport(format: ReportFormat, detail: ReportDetail, sales: any[], filters: SalesFilterSnapshot): void {
  const subtotalAll = sales.reduce((s, r) => s + (Array.isArray(r.Items) ? r.Items.reduce((a: number, it: any) => a + (Number(it["Sale Price"]) || 0), 0) : 0), 0);
  const feesAll     = sales.reduce((s, r) => s + (Number(r["Fees"]) || 0) + (Number(r["Shipping"]) || 0), 0);
  const cogsAll     = sales.reduce((s, r) => s + (Array.isArray(r.Items) ? r.Items.reduce((a: number, it: any) => a + (Number(it["COGS"]) || 0), 0) : 0), 0);
  const netAll      = sales.reduce((s, r) => s + (Number(r["Net Profit"]) || 0), 0);
  const stats = [
    { label: "Sales", value: fmtN(sales.length) },
    { label: "Gross", value: fmt$(subtotalAll) },
    { label: "Fees + Ship", value: fmt$(feesAll) },
    { label: "COGS", value: fmt$(cogsAll) },
    { label: "Net Profit", value: fmt$(netAll) },
  ];

  if (detail === "detailed") {
    // Item-level expansion: one row per Items[] entry across all sales.
    const flat: any[] = [];
    sales.forEach(s => {
      const items: any[] = Array.isArray(s.Items) ? s.Items : [];
      items.forEach(it => flat.push({ sale: s, item: it }));
    });
    const itemSubtotal = flat.reduce((s, x) => s + (Number(x.item["Sale Price"]) || 0), 0);
    const itemCOGS     = flat.reduce((s, x) => s + (Number(x.item["COGS"]) || 0), 0);
    const itemQty      = flat.reduce((s, x) => s + (Number(x.item.Qty) || 0), 0);
    const itemProfit   = +(itemSubtotal - itemCOGS).toFixed(2);

    runReport(format, {
      title: "Sales Detail (by line item)",
      fileSlug: "sales-detailed",
      filterSummary: summarizeSalesFilters(filters),
      rows: flat,
      orientation: "landscape",
      stats,
      columns: [
        { label: "Date",       get: x => fmtDate(x.sale["Date"]) || "" },
        { label: "Sale ID",    get: x => x.sale["Sale ID"] || "" },
        { label: "Platform",   get: x => x.sale["Platform"] || "" },
        { label: "Buyer",      get: x => x.sale["Buyer"] || "" },
        { label: "Lot ID",     get: x => x.item["Lot ID"] || "" },
        { label: "Item",       get: x => x.item.Item || "" },
        { label: "Type",       get: x => x.item.Type || "" },
        { label: "Qty",        align: "right", get: x => fmtN(x.item.Qty), footer: fmtN(itemQty) },
        { label: "Sale Price", align: "right", get: x => fmt$(x.item["Sale Price"]), footer: fmt$(itemSubtotal) },
        { label: "COGS",       align: "right", get: x => fmt$(x.item["COGS"]), footer: fmt$(itemCOGS) },
        { label: "Profit",     align: "right", get: x => fmt$((Number(x.item["Sale Price"]) || 0) - (Number(x.item["COGS"]) || 0)), footer: fmt$(itemProfit) },
      ],
    });
    return;
  }

  // Compact: one row per sale.
  runReport(format, {
    title: "Sales Summary",
    fileSlug: "sales-compact",
    filterSummary: summarizeSalesFilters(filters),
    rows: sales,
    stats,
    columns: [
      { label: "Sale ID",   get: r => r["Sale ID"] || "" },
      { label: "Date",      get: r => fmtDate(r["Date"]) || "" },
      { label: "Platform",  get: r => r["Platform"] || "" },
      { label: "Buyer",     get: r => r["Buyer"] || "" },
      { label: "Items",     align: "right", get: r => fmtN(Array.isArray(r.Items) ? r.Items.length : 0) },
      { label: "Subtotal",  align: "right", get: r => fmt$(Array.isArray(r.Items) ? r.Items.reduce((a: number, it: any) => a + (Number(it["Sale Price"]) || 0), 0) : 0), footer: fmt$(subtotalAll) },
      { label: "Fees+Ship", align: "right", get: r => fmt$((Number(r["Fees"]) || 0) + (Number(r["Shipping"]) || 0)), footer: fmt$(feesAll) },
      { label: "COGS",      align: "right", get: r => fmt$(Array.isArray(r.Items) ? r.Items.reduce((a: number, it: any) => a + (Number(it["COGS"]) || 0), 0) : 0), footer: fmt$(cogsAll) },
      { label: "Net",       align: "right", get: r => fmt$(r["Net Profit"]), footer: fmt$(netAll) },
    ],
  });
}

export function exportPurchasesReport(format: ReportFormat, detail: ReportDetail, purchases: any[], filters: PurchasesFilterSnapshot): void {
  const subtotalAll = purchases.reduce((s, r) => s + (Number(r["Subtotal"]) || 0), 0);
  const feesAll     = purchases.reduce((s, r) => s + (Number(r["Fees"]) || 0), 0);
  const paidAll     = purchases.reduce((s, r) => s + (Number(r["Total Paid"]) || 0), 0);
  const stats = [
    { label: "Purchases", value: fmtN(purchases.length) },
    { label: "Subtotal", value: fmt$(subtotalAll) },
    { label: "Fees", value: fmt$(feesAll) },
    { label: "Total Paid", value: fmt$(paidAll) },
  ];

  if (detail === "detailed") {
    const flat: any[] = [];
    purchases.forEach(p => {
      const items: any[] = Array.isArray(p.Items) ? p.Items : [];
      items.forEach(it => flat.push({ purchase: p, item: it }));
    });
    const itemQty   = flat.reduce((s, x) => s + (Number(x.item.Qty) || 0), 0);
    const itemCost  = flat.reduce((s, x) => s + (Number(x.item.UnitCost) || 0) * (Number(x.item.Qty) || 0), 0);

    runReport(format, {
      title: "Purchases Detail (by line item)",
      fileSlug: "purchases-detailed",
      filterSummary: summarizePurchasesFilters(filters),
      rows: flat,
      orientation: "landscape",
      stats,
      columns: [
        { label: "Date",       get: x => fmtDate(x.purchase["Date"]) || "" },
        { label: "Purchase",   get: x => x.purchase["Purchase ID"] || "" },
        { label: "Vendor",     get: x => x.purchase["Vendor"] || "" },
        { label: "Lot ID",     get: x => x.item["Lot ID"] || "" },
        { label: "Type",       get: x => x.item.Type || "" },
        { label: "Item",       get: x => x.item.Item || "" },
        { label: "Set",        get: x => x.item.Set || "" },
        { label: "#",          get: x => x.item.Number || "" },
        { label: "Cond/Grade", get: x => x.item.Condition || x.item.Grade || "" },
        { label: "Qty",        align: "right", get: x => fmtN(x.item.Qty), footer: fmtN(itemQty) },
        { label: "Unit Cost",  align: "right", get: x => fmt$(x.item.UnitCost) },
        { label: "Line",       align: "right", get: x => fmt$((Number(x.item.UnitCost) || 0) * (Number(x.item.Qty) || 0)), footer: fmt$(itemCost) },
      ],
    });
    return;
  }

  // Compact: one row per purchase.
  runReport(format, {
    title: "Purchases Summary",
    fileSlug: "purchases-compact",
    filterSummary: summarizePurchasesFilters(filters),
    rows: purchases,
    stats,
    columns: [
      { label: "Purchase ID", get: r => r["Purchase ID"] || "" },
      { label: "Date",        get: r => fmtDate(r["Date"]) || "" },
      { label: "Vendor",      get: r => r["Vendor"] || "" },
      { label: "Method",      get: r => r["Payment Method"] || "" },
      { label: "Items",       align: "right", get: r => fmtN(Array.isArray(r.Items) ? r.Items.length : 0) },
      { label: "Subtotal",    align: "right", get: r => fmt$(r["Subtotal"]), footer: fmt$(subtotalAll) },
      { label: "Fees",        align: "right", get: r => fmt$(r["Fees"]), footer: fmt$(feesAll) },
      { label: "Total Paid",  align: "right", get: r => fmt$(r["Total Paid"]), footer: fmt$(paidAll) },
    ],
  });
}

/* ---------- Tax-time bundle (year-end PDF) ---------- */

/** Year filter: row's date string is between Jan 1 and Dec 31 of `year` (ISO sortable). */
function inYear(dateStr: any, year: number): boolean {
  const s = String(dateStr || "");
  if (!s) return false;
  return s >= `${year}-01-01` && s <= `${year}-12-31`;
}

interface TaxSectionTable {
  columns: ReportColumn[];
  rows: any[];
  /** Optional footer with column totals already formatted. */
  footer?: string[];
}

function renderSectionTable(t: TaxSectionTable): string {
  const colHeaders = t.columns.map(c =>
    `<th style="text-align:${c.align === "right" ? "right" : "left"}">${esc(c.label)}</th>`
  ).join("");
  const bodyRows = t.rows.length
    ? t.rows.map(r =>
        `<tr>${t.columns.map(c =>
          `<td style="text-align:${c.align === "right" ? "right" : "left"}${c.align === "right" ? ";font-variant-numeric:tabular-nums" : ""}">${esc(c.get(r))}</td>`
        ).join("")}</tr>`
      ).join("")
    : `<tr><td colspan="${t.columns.length}" style="text-align:center;color:#94a3b8;padding:18px 8px">No rows.</td></tr>`;
  const footRow = t.footer
    ? `<tfoot><tr>${t.columns.map((c, i) =>
        `<td style="text-align:${c.align === "right" ? "right" : "left"};font-variant-numeric:tabular-nums"><b>${esc(t.footer![i] || "")}</b></td>`
      ).join("")}</tr></tfoot>`
    : "";
  return `
    <table class="tx">
      <thead><tr>${colHeaders}</tr></thead>
      <tbody>${bodyRows}</tbody>
      ${footRow}
    </table>`;
}

export function generateTaxBundle(year: number, db: {
  sales: any[]; purchases: any[]; expenses: any[]; mileage: any[];
  cards: any[]; sealed: any[]; trades?: any[]; transactions?: any[];
}, irsRate: number, bulkPools: Array<{ klass: string; qty: number; unitValue: number }>): void {
  const win = window.open("", "_blank", "width=1100,height=1100");
  if (!win) {
    toast("Pop-up blocked — allow pop-ups for this site to generate the tax bundle.", "err");
    return;
  }
  const generated = new Date().toLocaleString();

  // Filter every source to events that fall within the tax year.
  const yearSales     = db.sales.filter(r => inYear(r["Date"], year) && String(r["Status"] || "Completed") === "Completed");
  const yearPurchases = db.purchases.filter(r => inYear(r["Date"], year));
  const yearExpenses  = db.expenses.filter(r => inYear(r["Date"], year));
  const yearMileage   = db.mileage.filter(r => inYear(r["Date"], year));
  const yearTrades    = (db.trades || []).filter(r => inYear(r["Date"], year) && String(r["Status"] || "Accepted") === "Accepted");
  const yearAdjustments = (db.transactions || []).filter((r: any) => inYear(r["Date"], year));

  /* --- COVER STATS --- */
  const grossRevenue = yearSales.reduce((s, r) =>
    s + (Array.isArray(r.Items) ? r.Items.reduce((a: number, it: any) => a + (Number(it["Sale Price"]) || 0), 0) : 0), 0);
  const totalCOGS = yearSales.reduce((s, r) =>
    s + (Array.isArray(r.Items) ? r.Items.reduce((a: number, it: any) => a + (Number(it["COGS"]) || 0), 0) : 0), 0);
  const totalFeesShip = yearSales.reduce((s, r) => s + (Number(r["Fees"]) || 0) + (Number(r["Shipping"]) || 0), 0);
  const totalSpentOnPurchases = yearPurchases.reduce((s, r) => s + (Number(r["Total Paid"]) || 0), 0);
  const expensesDeductible = yearExpenses
    .filter(r => String(r["Deductible"] || "").toLowerCase().startsWith("y"))
    .reduce((s, r) => s + (Number(r["Amount"]) || 0), 0);
  const expensesAll = yearExpenses.reduce((s, r) => s + (Number(r["Amount"]) || 0), 0);
  const totalMiles = yearMileage.reduce((s, r) => s + (Number(r["Miles"]) || 0), 0);
  const mileageDeduction = yearMileage.reduce((s, r) => s + (Number(r["Deduction"]) || 0), 0);
  // Gifts & write-offs (from Adjustments). Deductible gift basis = Promotional
  // (advertising, at cost) + Business gift (subject to the $25/recipient/year
  // cap — flagged for the preparer, not auto-capped). Personal gifts are NOT
  // deductible and are excluded. Write-offs (damaged/lost/stolen inventory)
  // are deductible losses at basis.
  const giftDeductible = yearAdjustments
    .filter((r: any) => r["Type"] === "Gift" && ["Promotional / Prize", "Business gift (individual)"].includes(String(r["Tax Category"] || "")))
    .reduce((s: number, r: any) => s + (Number(r["Basis Removed"]) || 0), 0);
  const giftPersonal = yearAdjustments
    .filter((r: any) => r["Type"] === "Gift" && !["Promotional / Prize", "Business gift (individual)"].includes(String(r["Tax Category"] || "")))
    .reduce((s: number, r: any) => s + (Number(r["Basis Removed"]) || 0), 0);
  void giftPersonal; // excluded from deductions by design (see cover caveat)
  const writeOffLoss = yearAdjustments
    .filter((r: any) => r["Type"] === "Write-off")
    .reduce((s: number, r: any) => s + (Number(r["Basis Removed"]) || 0), 0);
  // Dealer treatment: a trade is a taxable disposition of inventory and its
  // gain is ordinary business income just like a sale. The amount realized on
  // the outgoing lots is the VALUE RECEIVED in exchange — incoming FMV plus
  // cash received (or minus cash paid; paid cash capitalizes into the incoming
  // lots' basis instead). This matches the realized gain booked on each trade
  // since the 2026-07-03 correction; the stored per-trade "Realized Gain" is
  // authoritative where present. (The old formula here used OUTGOING FMV as
  // proceeds — the exact pre-fix bug — and overstated trade income.)
  let tradeProceeds = 0, tradeCOGS = 0, tradeGain = 0;
  for (const tr of yearTrades) {
    const outs: any[] = Array.isArray(tr.Outgoing) ? tr.Outgoing : [];
    if (!outs.length) continue;
    const inc: any[] = Array.isArray(tr.Incoming) ? tr.Incoming : [];
    const cash = Number(tr["Cash"]) || 0; // + received / − paid
    const inFMV = inc.reduce((s: number, x: any) => s + (Number(x.FMV) || 0) * (Number(x.Qty) || 1), 0);
    const cogs = outs.reduce((s: number, it: any) => s + (Number(it.Basis) || 0) * (Number(it.Qty) || 1), 0);
    const proceeds = +(inFMV + cash).toFixed(2);
    const stored = tr["Realized Gain"];
    const gain = (stored != null && stored !== "" && Number.isFinite(Number(stored)))
      ? Number(stored)
      : +(proceeds - cogs).toFixed(2);
    tradeProceeds += proceeds;
    tradeCOGS     += cogs;
    tradeGain     += gain;
  }
  tradeProceeds = +tradeProceeds.toFixed(2);
  tradeCOGS = +tradeCOGS.toFixed(2);
  tradeGain = +tradeGain.toFixed(2);
  const approxNet = +(grossRevenue + tradeProceeds - totalCOGS - tradeCOGS - totalFeesShip - expensesDeductible - mileageDeduction - giftDeductible - writeOffLoss).toFixed(2);

  const coverStats = `
    <div class="kpi-grid">
      <div><div class="lbl">Gross Revenue (sales)</div><div class="val">${esc(fmt$(grossRevenue))}</div></div>
      <div><div class="lbl">COGS</div><div class="val">${esc(fmt$(totalCOGS))}</div></div>
      <div><div class="lbl">Fees + Shipping</div><div class="val">${esc(fmt$(totalFeesShip))}</div></div>
      <div><div class="lbl">Deductible Expenses</div><div class="val">${esc(fmt$(expensesDeductible))}</div></div>
      <div><div class="lbl">Mileage Deduction</div><div class="val">${esc(fmt$(mileageDeduction))}</div></div>
      <div><div class="lbl">Realized Trade Gains</div><div class="val">${esc(fmt$(tradeGain))}</div></div>
      ${(giftDeductible || writeOffLoss) ? `<div><div class="lbl">Gifts + Write-offs (deductible)</div><div class="val">${esc(fmt$(giftDeductible + writeOffLoss))}</div></div>` : ""}
      <div><div class="lbl ${approxNet >= 0 ? 'pos' : 'neg'}">Approx. Net Income</div><div class="val ${approxNet >= 0 ? 'pos' : 'neg'}">${esc(fmt$(approxNet))}</div></div>
    </div>
    <div class="caveat">Approx. Net Income = Gross Revenue + Trade Proceeds &minus; COGS &minus; Fees+Ship &minus; Deductible Expenses &minus; Mileage Deduction &minus; Deductible Gifts/Write-offs. Business gifts are subject to the $25/recipient/year cap (flagged, not auto-capped); personal gifts are excluded as non-deductible. Trades are counted as dealer (ordinary) income. This is an internal estimate; confirm specific Schedule&nbsp;C categorization with your tax advisor.</div>`;

  /* --- SECTION 1: SALES --- */
  const salesSection = renderSectionTable({
    columns: [
      { label: "Sale ID",   get: r => r["Sale ID"] || "" },
      { label: "Date",      get: r => fmtDate(r["Date"]) || "" },
      { label: "Platform",  get: r => r["Platform"] || "" },
      { label: "Buyer",     get: r => r["Buyer"] || "" },
      { label: "Items",     align: "right", get: r => fmtN(Array.isArray(r.Items) ? r.Items.length : 0) },
      { label: "Gross",     align: "right", get: r => fmt$(Array.isArray(r.Items) ? r.Items.reduce((a: number, it: any) => a + (Number(it["Sale Price"]) || 0), 0) : 0) },
      { label: "Fees+Ship", align: "right", get: r => fmt$((Number(r["Fees"]) || 0) + (Number(r["Shipping"]) || 0)) },
      { label: "COGS",      align: "right", get: r => fmt$(Array.isArray(r.Items) ? r.Items.reduce((a: number, it: any) => a + (Number(it["COGS"]) || 0), 0) : 0) },
      { label: "Net",       align: "right", get: r => fmt$(r["Net Profit"]) },
    ],
    rows: yearSales,
    footer: ["", "", "", "TOTAL", fmtN(yearSales.length), fmt$(grossRevenue), fmt$(totalFeesShip), fmt$(totalCOGS),
             fmt$(yearSales.reduce((s, r) => s + (Number(r["Net Profit"]) || 0), 0))],
  });

  /* --- SECTION 2: PURCHASES --- */
  const purchasesSection = renderSectionTable({
    columns: [
      { label: "Purchase ID", get: r => r["Purchase ID"] || "" },
      { label: "Date",        get: r => fmtDate(r["Date"]) || "" },
      { label: "Vendor",      get: r => r["Vendor"] || "" },
      { label: "Method",      get: r => r["Payment Method"] || "" },
      { label: "Items",       align: "right", get: r => fmtN(Array.isArray(r.Items) ? r.Items.length : 0) },
      { label: "Subtotal",    align: "right", get: r => fmt$(r["Subtotal"]) },
      { label: "Fees",        align: "right", get: r => fmt$(r["Fees"]) },
      { label: "Total Paid",  align: "right", get: r => fmt$(r["Total Paid"]) },
    ],
    rows: yearPurchases,
    footer: ["", "", "", "TOTAL", fmtN(yearPurchases.length),
             fmt$(yearPurchases.reduce((s, r) => s + (Number(r["Subtotal"]) || 0), 0)),
             fmt$(yearPurchases.reduce((s, r) => s + (Number(r["Fees"]) || 0), 0)),
             fmt$(totalSpentOnPurchases)],
  });

  /* --- SECTION 3: EXPENSES (grouped by category) --- */
  const byCategory = new Map<string, any[]>();
  yearExpenses.forEach(e => {
    const cat = e["Category"] || "(Uncategorized)";
    if (!byCategory.has(cat)) byCategory.set(cat, []);
    byCategory.get(cat)!.push(e);
  });
  const categoryBlocks = Array.from(byCategory.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([cat, rows]) => {
      const catTotal = rows.reduce((s, r) => s + (Number(r["Amount"]) || 0), 0);
      const catDeductible = rows
        .filter(r => String(r["Deductible"] || "").toLowerCase().startsWith("y"))
        .reduce((s, r) => s + (Number(r["Amount"]) || 0), 0);
      return `
        <div class="cat-block">
          <div class="cat-head"><b>${esc(cat)}</b> &middot; ${esc(rows.length.toString())} entries &middot; Total <b>${esc(fmt$(catTotal))}</b> &middot; Deductible <b>${esc(fmt$(catDeductible))}</b></div>
          ${renderSectionTable({
            columns: [
              { label: "Date",        get: r => fmtDate(r["Date"]) || "" },
              { label: "Vendor",      get: r => r["Vendor"] || "" },
              { label: "Amount",      align: "right", get: r => fmt$(r["Amount"]) },
              { label: "Deductible",  get: r => r["Deductible"] || "" },
              { label: "Method",      get: r => r["Payment Method"] || "" },
              { label: "Notes",       get: r => r["Notes"] || "" },
            ],
            rows,
          })}
        </div>`;
    }).join("");
  const expensesSection = `
    <div class="section-summary">Year total: <b>${esc(fmt$(expensesAll))}</b> across ${esc(yearExpenses.length.toString())} entries &middot; Deductible: <b>${esc(fmt$(expensesDeductible))}</b></div>
    ${categoryBlocks || `<div class="empty">No expenses logged for ${esc(year.toString())}.</div>`}`;

  /* --- SECTION 4: MILEAGE --- */
  const mileageSection = `
    <div class="section-summary">${esc(yearMileage.length.toString())} trips &middot; <b>${esc(fmtN(totalMiles))}</b> miles &middot; IRS rate <b>${esc(`$${irsRate.toFixed(2)}/mi`)}</b> &middot; Deduction <b>${esc(fmt$(mileageDeduction))}</b></div>
    ${renderSectionTable({
      columns: [
        { label: "Date",      get: r => fmtDate(r["Date"]) || "" },
        { label: "Purpose",   get: r => r["Purpose"] || "" },
        { label: "Start Odo", align: "right", get: r => fmtN(r["Start Odo"]) },
        { label: "End Odo",   align: "right", get: r => fmtN(r["End Odo"]) },
        { label: "Miles",     align: "right", get: r => fmtN(r["Miles"]) },
        { label: "Rate",      align: "right", get: r => fmt$(r["IRS Rate"]) },
        { label: "Deduction", align: "right", get: r => fmt$(r["Deduction"]) },
        { label: "Related",   get: r => r["Related Deal/Lot"] || "" },
      ],
      rows: yearMileage,
      footer: ["", "", "", "TOTAL", fmtN(totalMiles), "", fmt$(mileageDeduction), ""],
    })}`;

  /* --- SECTION 5: REALIZED CAPITAL GAINS (sales + trade dispositions) --- */
  // Each disposition line - one per sale item and per outgoing trade item -
  // gets normalized to a common shape for Schedule D / Form 8949 mapping.
  // Free-text items or items missing an Acq Date show as "Unknown" so the
  // user can audit and fix before filing.
  interface DispRow {
    date: string; source: string; lotId: string; item: string;
    acqDate: string; days: number | null; treatment: string;
    proceeds: number; basis: number; gain: number;
  }
  const dispositions: DispRow[] = [];
  for (const sale of yearSales) {
    const items: any[] = Array.isArray(sale.Items) ? sale.Items : [];
    for (const it of items) {
      dispositions.push({
        date:      sale["Date"] || "",
        source:    sale["Sale ID"] || "",
        lotId:     it["Lot ID"] || "(free-text)",
        item:      it["Item"] || "",
        acqDate:   it["Acq Date"] || "",
        days:      it["Holding Days"] ?? null,
        treatment: it["Tax Treatment"] || "Unknown",
        proceeds:  Number(it["Sale Price"]) || 0,
        basis:     Number(it["COGS"]) || 0,
        gain:      Number(it["Realized Gain"] ?? ((Number(it["Sale Price"]) || 0) - (Number(it["COGS"]) || 0))),
      });
    }
  }
  for (const tr of yearTrades) {
    const items: any[] = Array.isArray(tr.Outgoing) ? tr.Outgoing : [];
    for (const it of items) {
      const qty = Number(it.Qty) || 1;
      const basis = (Number(it.Basis) || 0) * qty;
      // Per-line proceeds = the line's share of value received. The booked
      // per-line Realized Gain (proceeds share − basis) is authoritative, so
      // proceeds = basis + gain; the outgoing FMV is only the legacy fallback
      // for lines that predate the 2026-07-03 gain correction.
      const storedGain = it["Realized Gain"];
      const hasStored = storedGain != null && storedGain !== "" && Number.isFinite(Number(storedGain));
      const gain = hasStored ? Number(storedGain) : +(((Number(it.FMV) || 0) * qty) - basis).toFixed(2);
      const proceeds = +(basis + gain).toFixed(2);
      dispositions.push({
        date:      tr["Date"] || "",
        source:    tr["Trade ID"] || "",
        lotId:     it["Lot ID"] || "(free-text)",
        item:      it["Item"] || "",
        acqDate:   it["Acq Date"] || "",
        days:      it["Holding Days"] ?? null,
        treatment: it["Tax Treatment"] || "Unknown",
        proceeds,
        basis,
        gain,
      });
    }
  }
  dispositions.sort((a, b) => String(a.date).localeCompare(String(b.date)));
  const shortTermDisp = dispositions.filter(d => d.treatment === "Short-term");
  const longTermDisp  = dispositions.filter(d => d.treatment === "Long-term");
  const unknownDisp   = dispositions.filter(d => d.treatment === "Unknown");
  const sumGain = (arr: DispRow[]) => arr.reduce((s, d) => s + d.gain, 0);

  const dispCols = [
    { label: "Date",      get: (d: DispRow) => fmtDate(d.date) || "" },
    { label: "Source",    get: (d: DispRow) => d.source },
    { label: "Lot ID",    get: (d: DispRow) => d.lotId },
    { label: "Item",      get: (d: DispRow) => d.item },
    { label: "Acquired",  get: (d: DispRow) => fmtDate(d.acqDate) || "-" },
    { label: "Days",      align: "right" as const, get: (d: DispRow) => d.days == null ? "-" : String(d.days) },
    { label: "Proceeds",  align: "right" as const, get: (d: DispRow) => fmt$(d.proceeds) },
    { label: "Basis",     align: "right" as const, get: (d: DispRow) => fmt$(d.basis) },
    { label: "Gain/Loss", align: "right" as const, get: (d: DispRow) => fmt$(d.gain) },
  ];
  const dispositionsSection = `
    <div class="caveat">Each row is a taxable disposition (sale item or trade-outgoing item). Short-term = held &le; 365 days; Long-term = held &gt; 365 days. Unknown rows lack an Acq Date on the source lot &mdash; fix those before filing Schedule D / Form 8949.</div>
    <div class="cat-block">
      <div class="cat-head"><b>Short-term</b> &middot; ${esc(shortTermDisp.length.toString())} dispositions &middot; Gain/Loss <b>${esc(fmt$(sumGain(shortTermDisp)))}</b></div>
      ${shortTermDisp.length
        ? renderSectionTable({ columns: dispCols, rows: shortTermDisp,
            footer: ["", "", "", "", "TOTAL", "",
                     fmt$(shortTermDisp.reduce((s, d) => s + d.proceeds, 0)),
                     fmt$(shortTermDisp.reduce((s, d) => s + d.basis, 0)),
                     fmt$(sumGain(shortTermDisp))] })
        : `<div class="empty">No short-term dispositions.</div>`}
    </div>
    <div class="cat-block">
      <div class="cat-head"><b>Long-term</b> &middot; ${esc(longTermDisp.length.toString())} dispositions &middot; Gain/Loss <b>${esc(fmt$(sumGain(longTermDisp)))}</b></div>
      ${longTermDisp.length
        ? renderSectionTable({ columns: dispCols, rows: longTermDisp,
            footer: ["", "", "", "", "TOTAL", "",
                     fmt$(longTermDisp.reduce((s, d) => s + d.proceeds, 0)),
                     fmt$(longTermDisp.reduce((s, d) => s + d.basis, 0)),
                     fmt$(sumGain(longTermDisp))] })
        : `<div class="empty">No long-term dispositions.</div>`}
    </div>
    ${unknownDisp.length ? `
    <div class="cat-block">
      <div class="cat-head" style="background:#fef3c7;border-left-color:#ca8a04"><b>Unknown holding period</b> &middot; ${esc(unknownDisp.length.toString())} dispositions &middot; Gain/Loss <b>${esc(fmt$(sumGain(unknownDisp)))}</b> &middot; <em>Fix Acq Date on source lots</em></div>
      ${renderSectionTable({ columns: dispCols, rows: unknownDisp,
        footer: ["", "", "", "", "TOTAL", "",
                 fmt$(unknownDisp.reduce((s, d) => s + d.proceeds, 0)),
                 fmt$(unknownDisp.reduce((s, d) => s + d.basis, 0)),
                 fmt$(sumGain(unknownDisp))] })}
    </div>` : ""}`;

  /* --- SECTION 6: INVENTORY SNAPSHOT (current state) --- */
  const cardLots   = db.cards.filter((r: any) => !(r["Card Lot ID"] === "BULK" || r["Card Lot ID"] === "HOLO-BULK" || r["Card Lot ID"] === "PREM-BULK" || r["Card Lot ID"] === "BULK-1-5" || r["Card Lot ID"] === "BULK-5-10"));
  const cardsQty   = cardLots.reduce((s: number, r: any) => s + (Number(r["Qty"]) || 0), 0);
  const cardsBasis = cardLots.reduce((s: number, r: any) => s + (Number(r["Total Basis"]) || 0), 0);
  const cardsFMV   = cardLots.reduce((s: number, r: any) => s + (Number(r["Current FMV"]) || 0), 0);
  const sealedQty   = db.sealed.reduce((s: number, r: any) => s + (Number(r["Qty"]) || 0), 0);
  const sealedBasis = db.sealed.reduce((s: number, r: any) => s + (Number(r["Total Basis"]) || 0), 0);
  const sealedFMV   = db.sealed.reduce((s: number, r: any) => s + (Number(r["Current FMV"]) || 0), 0);
  const bulkQty   = bulkPools.reduce((s, p) => s + p.qty, 0);
  const bulkBasis = bulkPools.reduce((s, p) => s + p.qty * p.unitValue, 0);

  const inventorySection = `
    <div class="caveat">Inventory snapshot is <b>current</b> (as of generation timestamp), not Dec 31 of ${esc(year.toString())}. For accurate year-end inventory, generate this bundle close to January 1.</div>
    <div class="inv-grid">
      <div class="inv-block">
        <div class="inv-title">Cards (singles, slabs)</div>
        <div>Lots: <b>${esc(fmtN(cardLots.length))}</b></div>
        <div>Cards: <b>${esc(fmtN(cardsQty))}</b></div>
        <div>Basis: <b>${esc(fmt$(cardsBasis))}</b></div>
        <div>FMV: <b>${esc(fmt$(cardsFMV))}</b></div>
      </div>
      <div class="inv-block">
        <div class="inv-title">Sealed product</div>
        <div>Lots: <b>${esc(fmtN(db.sealed.length))}</b></div>
        <div>Units: <b>${esc(fmtN(sealedQty))}</b></div>
        <div>Basis: <b>${esc(fmt$(sealedBasis))}</b></div>
        <div>FMV: <b>${esc(fmt$(sealedFMV))}</b></div>
      </div>
      <div class="inv-block">
        <div class="inv-title">Bulk pools</div>
        ${bulkPools.map(p => `<div>${esc(p.klass)}: <b>${esc(fmtN(p.qty))}</b> @ ${esc(fmt$(p.unitValue))} = <b>${esc(fmt$(p.qty * p.unitValue))}</b></div>`).join("")}
        <div style="border-top:1px solid #cbd5e1;margin-top:4px;padding-top:4px">Total bulk: <b>${esc(fmtN(bulkQty))}</b> cards &middot; <b>${esc(fmt$(bulkBasis))}</b></div>
      </div>
    </div>
    <div class="inv-totals">
      Total inventory basis: <b>${esc(fmt$(cardsBasis + sealedBasis + bulkBasis))}</b> &middot;
      Total inventory FMV (excl. bulk): <b>${esc(fmt$(cardsFMV + sealedFMV))}</b>
    </div>`;

  /* --- FINANCIAL STATEMENTS (P&L for the year; balance sheet = current snapshot) --- */
  const feesOnly = yearSales.reduce((s, r) => s + (Number(r["Fees"]) || 0), 0);
  const shipOnly = yearSales.reduce((s, r) => s + (Number(r["Shipping"]) || 0), 0);
  const grossProfit = +(grossRevenue + tradeProceeds - totalCOGS - tradeCOGS).toFixed(2);

  const catDeductible: Record<string, number> = {};
  for (const e of yearExpenses) {
    if (!String(e["Deductible"] || "").toLowerCase().startsWith("y")) continue;
    const cat = e["Category"] || "(Uncategorized)";
    catDeductible[cat] = (catDeductible[cat] || 0) + (Number(e["Amount"]) || 0);
  }
  const catNames = Object.keys(catDeductible).sort();

  const plLine = (label: string, amt: number, cls = "") =>
    `<tr class="${cls}"><td>${esc(label)}</td><td style="text-align:right">${esc(fmt$(amt))}</td></tr>`;

  const plSection = `
    <div class="caveat">Income statement for ${esc(year.toString())} on the matching basis the app uses (COGS recognized when an item sells). Profit on card sales and trades is shown here as ordinary business income (Schedule C, dealer treatment). If you file as an <b>investor</b> rather than a <b>dealer</b>, those gains belong on Schedule D instead (see section 5) &mdash; they are two different treatments, don't apply both. Confirm with your tax advisor.</div>
    <table class="tx stmt"><tbody>
      ${plLine("Gross sales (revenue)", grossRevenue)}
      ${tradeProceeds ? plLine("Trade proceeds (value received: incoming FMV \u00b1 cash boot)", tradeProceeds) : ""}
      ${plLine("Less: cost of goods sold (sales + trades)", -(totalCOGS + tradeCOGS))}
      ${giftDeductible ? plLine("Less: deductible gifts (promotional + business, at basis)", -giftDeductible) : ""}
      ${writeOffLoss ? plLine("Less: inventory write-offs (at basis)", -writeOffLoss) : ""}
      ${plLine("Gross profit", grossProfit, "sub")}
      ${plLine("Less: marketplace fees", -feesOnly)}
      ${plLine("Less: shipping on sales", -shipOnly)}
      ${catNames.map(c => plLine(`Less: ${c}`, -catDeductible[c])).join("")}
      ${plLine("Less: mileage deduction", -mileageDeduction)}
      ${plLine("Net income (loss)", approxNet, "tot")}
    </tbody></table>`;

  const SCHED_C: Record<string, string> = {
    "Wages": "Line 26 - Wages",
    "Contract labor": "Line 11 - Contract labor",
    "Payroll taxes": "Line 23 - Taxes & licenses",
    "Grading": "Line 27a - Other (grading)",
    "Shipping": "Line 27a - Other (postage & shipping)",
    "Supplies": "Line 22 - Supplies",
    "Show Fees": "Line 27a - Other (show/booth fees)",
    "LLC Costs": "Line 23 - Taxes & licenses",
    "Software": "Line 27a - Other (software)",
    "Insurance": "Line 15 - Insurance",
    "Storage": "Line 20b - Rent/lease (other property)",
    "Mileage": "Line 9 - Car & truck",
    "Other": "Line 27a - Other expenses",
  };
  const lineAgg: Record<string, number> = {};
  for (const c of catNames) {
    const line = SCHED_C[c] || "Line 27a - Other expenses";
    lineAgg[line] = (lineAgg[line] || 0) + catDeductible[c];
  }
  if (mileageDeduction) lineAgg["Line 9 - Car & truck"] = (lineAgg["Line 9 - Car & truck"] || 0) + mileageDeduction;
  if (feesOnly) lineAgg["Line 10 - Commissions & fees"] = (lineAgg["Line 10 - Commissions & fees"] || 0) + feesOnly;
  if (shipOnly) lineAgg["Line 27a - Other (postage & shipping)"] = (lineAgg["Line 27a - Other (postage & shipping)"] || 0) + shipOnly;
  const lineNames = Object.keys(lineAgg).sort();
  const totalSchedCExpenses = +lineNames.reduce((s, k) => s + lineAgg[k], 0).toFixed(2);
  const schedCNet = +(grossProfit - totalSchedCExpenses).toFixed(2);

  const scSection = `
    <div class="caveat"><b>Suggested</b> Schedule C (Form 1040) placement &mdash; a starting point, not tax advice. Your tax pro should confirm each line, especially anything under "Other expenses (Part V)". This view treats you as a <b>dealer</b> (ordinary income); investors use Schedule D (section 5) instead.</div>
    <table class="tx stmt"><tbody>
      ${plLine("Line 1 - Gross receipts / sales (incl. trade proceeds)", grossRevenue + tradeProceeds)}
      ${plLine("Line 4 - Cost of goods sold (Part III)", totalCOGS + tradeCOGS)}
      ${plLine("Line 7 - Gross income", grossProfit, "sub")}
      ${lineNames.map(k => plLine(k, lineAgg[k])).join("")}
      ${plLine("Line 28 - Total expenses", totalSchedCExpenses, "sub")}
      ${plLine("Line 31 - Net profit (loss)", schedCNet, "tot")}
    </tbody></table>
    <div class="section-summary">Part III (COGS) memo: purchases during ${esc(year.toString())} = <b>${esc(fmt$(totalSpentOnPurchases))}</b>; current inventory at cost = <b>${esc(fmt$(cardsBasis + sealedBasis + bulkBasis))}</b>. The app books COGS per item as it sells (Line 4 above); if your accountant uses beginning-inventory + purchases - ending-inventory instead, these figures help reconcile.</div>`;

  const invAtCost = +(cardsBasis + sealedBasis + bulkBasis).toFixed(2);
  const invAtFMV = +(cardsFMV + sealedFMV).toFixed(2);
  const cashNow = cashOnHand();
  const totalAssets = +(cashNow + invAtCost).toFixed(2);
  const dueOwner = ownerLoanBalance();
  const ownerEquity = +(totalAssets - dueOwner).toFixed(2);

  const bsSection = `
    <div class="caveat">A simplified <b>current</b> position snapshot (as of generation, not Dec 31 of ${esc(year.toString())}). Inventory is valued at cost. "Due to Owner" is what the business still owes you for contributions &mdash; your accountant will classify it as a loan (liability) or as equity. This is not a GAAP double-entry balance sheet and may not tie to the penny without an opening-equity figure.</div>
    <table class="tx stmt"><tbody>
      <tr class="sub"><td colspan="2">Assets</td></tr>
      ${plLine("Cash on hand", cashNow)}
      ${plLine("Inventory at cost", invAtCost)}
      ${plLine("Total assets", totalAssets, "sub")}
      <tr class="sub"><td colspan="2">Less: amounts owed</td></tr>
      ${plLine("Due to owner (unrepaid contributions)", dueOwner)}
      ${plLine("Owner's equity (net business stake)", ownerEquity, "tot")}
    </tbody></table>
    <div class="section-summary">Memo: inventory at current FMV (excl. bulk) is <b>${esc(fmt$(invAtFMV))}</b>. Appreciation over cost isn't income until you sell.</div>`;

  const html = `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<title>${esc(`Tax Bundle ${year}`)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; margin: 0; padding: 20px 28px; color: #0f172a; background: #ffffff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  h1 { font-size: 22pt; margin: 0 0 4px; }
  h2 { font-size: 14pt; margin: 0 0 8px; padding-bottom: 6px; border-bottom: 2px solid #64748b; color: #0f172a; }
  .meta { font-size: 9pt; color: #64748b; margin-bottom: 18px; }
  .kpi-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin: 12px 0 16px; }
  .kpi-grid > div { padding: 12px 14px; background: #f1f5f9; border: 1px solid #e2e8f0; border-radius: 6px; }
  .kpi-grid .lbl { font-size: 9pt; color: #64748b; text-transform: uppercase; letter-spacing: 0.04em; }
  .kpi-grid .val { font-size: 16pt; font-weight: 700; font-variant-numeric: tabular-nums; margin-top: 2px; }
  .kpi-grid .pos { color: #047857; }
  .kpi-grid .neg { color: #b91c1c; }
  .caveat { font-size: 8pt; color: #64748b; font-style: italic; margin: 6px 0 14px; padding: 6px 10px; background: #fefce8; border-left: 3px solid #ca8a04; }
  .section { margin-top: 26px; page-break-inside: avoid; }
  .section.break { page-break-before: always; }
  .section-summary { font-size: 10pt; color: #475569; margin: 6px 0 10px; padding: 6px 10px; background: #f8fafc; border-left: 3px solid #94a3b8; }
  .cat-block { margin: 10px 0; }
  .cat-head { font-size: 10pt; margin: 8px 0 4px; padding: 4px 8px; background: #ecfdf5; border-left: 3px solid #047857; }
  .empty { font-size: 10pt; color: #94a3b8; padding: 12px; text-align: center; font-style: italic; }
  table.tx { width: 100%; border-collapse: collapse; font-size: 8.5pt; margin-bottom: 4px; }
  table.tx thead th { background: #f1f5f9; border-bottom: 2px solid #64748b; padding: 4px 6px; text-align: left; font-weight: 600; }
  table.tx tbody td { border-bottom: 1px solid #e2e8f0; padding: 3px 6px; vertical-align: top; }
  table.tx tbody tr:nth-child(even) td { background: #fafbfc; }
  table.tx tfoot td { border-top: 2px solid #64748b; padding: 4px 6px; background: #f1f5f9; }
  table.tx tbody tr { page-break-inside: avoid; }
  table.stmt { max-width: 560px; }
  table.stmt td { padding: 4px 10px; font-size: 9.5pt; }
  table.stmt tr.sub td { font-weight: 700; border-top: 1px solid #cbd5e1; background: #f8fafc; }
  table.stmt tr.tot td { font-weight: 700; border-top: 2px solid #64748b; background: #f1f5f9; font-size: 10.5pt; }
  .inv-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 12px; margin-top: 12px; font-size: 10pt; }
  .inv-block { padding: 12px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; }
  .inv-title { font-weight: 700; margin-bottom: 6px; color: #0f172a; }
  .inv-totals { margin-top: 12px; padding: 8px 12px; background: #f1f5f9; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 10pt; }
  .toolbar { position: fixed; top: 8px; right: 8px; display: flex; gap: 8px; }
  .toolbar button { padding: 6px 14px; font-size: 10pt; border: 1px solid #cbd5e1; background: #ffffff; border-radius: 4px; cursor: pointer; }
  .toolbar button:hover { background: #f1f5f9; }
  @media print {
    .toolbar { display: none !important; }
    @page { size: letter portrait; margin: 0.45in; }
    body { padding: 0; }
  }
</style>
</head>
<body>
  <div class="toolbar">
    <button onclick="window.print()">🖨 Print / Save as PDF</button>
    <button onclick="window.close()">Close</button>
  </div>

  <h1>Tax Bundle &mdash; ${esc(year.toString())}</h1>
  <div class="meta">Generated ${esc(generated)} &middot; Enigma Ledger</div>

  <div class="section">
    <h2>Summary</h2>
    ${coverStats}
  </div>

  <div class="section break">
    <h2>Income Statement (P&amp;L) &mdash; ${esc(year.toString())}</h2>
    ${plSection}
  </div>

  <div class="section break">
    <h2>Schedule C Mapping (suggested)</h2>
    ${scSection}
  </div>

  <div class="section break">
    <h2>Balance Sheet (current snapshot)</h2>
    ${bsSection}
  </div>

  <div class="section break">
    <h2>1. Sales (${esc(yearSales.length.toString())} sales)</h2>
    ${salesSection}
  </div>

  <div class="section break">
    <h2>2. Purchases (${esc(yearPurchases.length.toString())} purchases)</h2>
    ${purchasesSection}
  </div>

  <div class="section break">
    <h2>3. Expenses (${esc(yearExpenses.length.toString())} entries across ${esc(byCategory.size.toString())} categories)</h2>
    ${expensesSection}
  </div>

  <div class="section break">
    <h2>4. Vehicle / Mileage Log (${esc(yearMileage.length.toString())} trips)</h2>
    ${mileageSection}
  </div>

  <div class="section break">
    <h2>5. Realized Capital Gains (${esc(dispositions.length.toString())} dispositions: ${esc(shortTermDisp.length.toString())} short / ${esc(longTermDisp.length.toString())} long${unknownDisp.length ? ` / ${esc(unknownDisp.length.toString())} unknown` : ""})</h2>
    ${dispositionsSection}
  </div>

  <div class="section break">
    <h2>6. Inventory Snapshot</h2>
    ${inventorySection}
  </div>

  <script>
    window.addEventListener("load", () => { setTimeout(() => window.print(), 250);  });
  </script>
</body>
</html>`;

  win.document.open();
  win.document.write(html);
  win.document.close();
}

/* ---------- Export Options modal ---------- */

/**
 * Open a small modal asking for Format + Detail. On submit, calls `onRun`
 * with the chosen values. Caller provides the per-table run function.
 */
export function openExportOptions(
  contextLabel: string,
  onRun: (format: ReportFormat, detail: ReportDetail) => void,
): void {
  const body = `
    <div class="space-y-4">
      <div class="text-sm text-[var(--muted)]">Exporting <b>${escapeHtml(contextLabel)}</b> &mdash; current filters preserved.</div>
      <div>
        <label class="label" for="exportFormat">Format</label>
        <select class="select" id="exportFormat">
          <option value="pdf">PDF (open print dialog / Save as PDF)</option>
          <option value="csv">CSV (download spreadsheet file)</option>
        </select>
      </div>
      <div>
        <label class="label" for="exportDetail">Detail level</label>
        <select class="select" id="exportDetail">
          <option value="compact">Compact &mdash; essentials only (Lot ID, Name, Set, Cond, Qty, FMV)</option>
          <option value="detailed">Detailed &mdash; all columns (basis, FMV, G/L, source, dates&hellip;)</option>
        </select>
        <div class="text-xs text-[var(--muted)] mt-1">Detailed PDFs auto-switch to landscape orientation so all columns fit.</div>
      </div>
    </div>`;
  const footer = `
    <button class="btn btn-ghost" onclick="closeModal()">Cancel</button>
    <button class="btn btn-primary" id="exportRunBtn">Export</button>`;
  openModal({ title: "Export Inventory", body, footer });

  // Wire submit — pull values, dispatch, close.
  setTimeout(() => {
    const btn = document.getElementById("exportRunBtn");
    btn?.addEventListener("click", () => {
      const fmt = (document.getElementById("exportFormat") as HTMLSelectElement | null)?.value as ReportFormat;
      const dtl = (document.getElementById("exportDetail") as HTMLSelectElement | null)?.value as ReportDetail;
      closeModal();
      onRun(fmt || "pdf", dtl || "compact");
    });
  }, 0);
}

// Inline-handler bridges
(window as any).exportCardsReport   = exportCardsReport;
(window as any).exportSealedReport  = exportSealedReport;
(window as any).openExportOptions   = openExportOptions;
(window as any).exportSalesReport     = exportSalesReport;
(window as any).exportPurchasesReport = exportPurchasesReport;
(window as any).generateTaxBundle      = generateTaxBundle;



/* ============================================================
   Trade Proposal printable - two side-by-side item tables with
   FMV totals, cash boot, side totals, and Net G/L. Works for any
   trade status; primary use is to share a "Proposed" trade with
   the other party.
   ============================================================ */
export function exportTradeProposal(tradeOrId: string | any): void {
  // Accept either a saved trade's ID (used by the row print buttons) or an
  // already-built trade object (used by the form's "Print" button so it works
  // on unsaved drafts). Object shape: { Trade ID, Date, Status, Notes, Cash,
  // Outgoing[], Incoming[] }.
  const trade = typeof tradeOrId === "string"
    ? DB.table("trades").find(r => r["Trade ID"] === tradeOrId)
    : tradeOrId;
  if (!trade) { toast("Trade not found", "err"); return; }
  const tradeId = String(trade["Trade ID"] || "(unsaved)");

  const out: any[] = Array.isArray(trade.Outgoing) ? trade.Outgoing : [];
  const inc: any[] = Array.isArray(trade.Incoming) ? trade.Incoming : [];
  const cash = Number(trade["Cash"]) || 0;
  const cashReceived = cash > 0 ? cash : 0;
  const cashPaid     = cash < 0 ? -cash : 0;

  const outFMV = out.reduce((a: number, x: any) => a + (Number(x.FMV) || 0) * (Number(x.Qty) || 1), 0);
  const inFMV  = inc.reduce((a: number, x: any) => a + (Number(x.FMV) || 0) * (Number(x.Qty) || 1), 0);
  const yourSideTotal  = +(outFMV + cashPaid).toFixed(2);
  const theirSideTotal = +(inFMV + cashReceived).toFixed(2);
  const netGL = +(inFMV - outFMV + cash).toFixed(2);
  const status = String(trade["Status"] || "Accepted");
  const statusLabel = status === "Proposed" ? "Pending"
                    : status === "Accepted" ? "Completed"
                    : status;
  const pageHeading = status === "Proposed" ? "Trade Proposal"
                    : status === "Accepted" ? "Trade Receipt"
                    : "Trade";

  const win = window.open("", "_blank", "width=1100,height=1100");
  if (!win) {
    toast("Pop-up blocked - allow pop-ups for this site to use Export.", "err");
    return;
  }

  const itemRow = (x: any): string => {
    const qty = Number(x.Qty) || 1;
    const unit = Number(x.FMV) || 0;
    const total = +(unit * qty).toFixed(2);
    return '<tr>' +
      '<td>' + esc(x.Item || x["Lot ID"] || "-") + '</td>' +
      '<td style="text-align:right;font-variant-numeric:tabular-nums">' + esc(fmtN(qty)) + '</td>' +
      '<td style="text-align:right;font-variant-numeric:tabular-nums">' + esc(fmt$(unit)) + '</td>' +
      '<td style="text-align:right;font-variant-numeric:tabular-nums">' + esc(fmt$(total)) + '</td>' +
    '</tr>';
  };

  const outRows = out.length ? out.map(itemRow).join("") :
    '<tr><td colspan="4" style="text-align:center;color:#94a3b8;padding:16px">No outgoing items.</td></tr>';
  const incRows = inc.length ? inc.map(itemRow).join("") :
    '<tr><td colspan="4" style="text-align:center;color:#94a3b8;padding:16px">No incoming items.</td></tr>';

  const cashLabel = cash === 0 ? "" :
    cash < 0 ? 'You pay <b>' + esc(fmt$(cashPaid)) + '</b> cash boot'
             : 'You receive <b>' + esc(fmt$(cashReceived)) + '</b> cash boot';

  const generated = new Date().toLocaleString();
  const badgeClass = status.toLowerCase();

  const html = '<!doctype html>\n' +
'<html><head><meta charset="utf-8"><title>' + esc(pageHeading) + ' ' + esc(tradeId) + '</title>\n' +
'<style>\n' +
'  * { box-sizing: border-box; }\n' +
'  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; margin: 0; padding: 18px 24px; color: #0f172a; background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }\n' +
'  h1 { font-size: 18pt; margin: 0 0 4px; }\n' +
'  .meta { font-size: 9pt; color: #64748b; margin-bottom: 14px; }\n' +
'  .badge { display: inline-block; padding: 2px 10px; border-radius: 999px; font-size: 9pt; font-weight: 600; margin-left: 8px; vertical-align: middle; }\n' +
'  .badge.proposed  { background: #fef3c7; color: #92400e; border: 1px solid #fbbf24; }\n' +
'  .badge.accepted  { background: #d1fae5; color: #065f46; border: 1px solid #10b981; }\n' +
'  .badge.declined  { background: #fee2e2; color: #991b1b; border: 1px solid #ef4444; }\n' +
'  .badge.cancelled { background: #e2e8f0; color: #475569; border: 1px solid #94a3b8; }\n' +
'  .sides { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }\n' +
'  .side h2 { font-size: 13pt; margin: 0 0 6px; padding-bottom: 4px; border-bottom: 2px solid #94a3b8; }\n' +
'  .side.you  h2 { color: #b45309; }\n' +
'  .side.them h2 { color: #0e7490; }\n' +
'  table { width: 100%; border-collapse: collapse; font-size: 10pt; }\n' +
'  thead th { background: #f1f5f9; border-bottom: 1px solid #cbd5e1; padding: 5px 7px; text-align: left; font-weight: 600; }\n' +
'  thead th.r { text-align: right; }\n' +
'  tbody td { border-bottom: 1px solid #e2e8f0; padding: 4px 7px; vertical-align: top; }\n' +
'  tfoot td { border-top: 2px solid #64748b; padding: 6px 7px; background: #f8fafc; font-weight: 600; }\n' +
'  .totals { margin-top: 18px; padding: 12px 14px; background: #f1f5f9; border: 1px solid #cbd5e1; border-radius: 6px; }\n' +
'  .totals .row { display: flex; justify-content: space-between; margin: 4px 0; font-size: 11pt; }\n' +
'  .totals .row.net { font-size: 13pt; font-weight: 700; padding-top: 8px; border-top: 1px solid #cbd5e1; margin-top: 8px; }\n' +
'  .totals .row.net.pos { color: #065f46; }\n' +
'  .totals .row.net.neg { color: #991b1b; }\n' +
'  .notes { margin-top: 18px; font-size: 9pt; white-space: pre-wrap; padding: 10px 12px; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; }\n' +
'  .toolbar { position: fixed; top: 8px; right: 8px; display: flex; gap: 8px; }\n' +
'  .toolbar button { padding: 6px 14px; font-size: 10pt; border: 1px solid #cbd5e1; background: #ffffff; border-radius: 4px; cursor: pointer; }\n' +
'  @media print { .toolbar { display: none !important; } @page { size: letter portrait; margin: 0.4in; } body { padding: 0; } }\n' +
'</style>\n' +
'</head><body>\n' +
'  <div class="toolbar">\n' +
'    <button onclick="window.print()">\u{1F5A8} Print / Save as PDF</button>\n' +
'    <button onclick="window.close()">Close</button>\n' +
'  </div>\n' +
'  <h1>' + esc(pageHeading) + ' <span style="color:#64748b">' + esc(tradeId) + '</span><span class="badge ' + esc(badgeClass) + '">' + esc(statusLabel) + '</span></h1>\n' +
'  <div class="meta">Date ' + esc(fmtDate(trade["Date"])) + ' &middot; Generated ' + esc(generated) + '</div>\n' +
'  <div class="sides">\n' +
'    <div class="side you">\n' +
'      <h2>You give</h2>\n' +
'      <table>\n' +
'        <thead><tr><th>Item</th><th class="r">Qty</th><th class="r">FMV / unit</th><th class="r">Line FMV</th></tr></thead>\n' +
'        <tbody>' + outRows + '</tbody>\n' +
'        <tfoot>' +
'          <tr><td colspan="3" style="text-align:right">Outgoing FMV</td><td style="text-align:right;font-variant-numeric:tabular-nums">' + esc(fmt$(outFMV)) + '</td></tr>' +
          (cashPaid > 0 ? '<tr><td colspan="3" style="text-align:right">+ Cash you pay</td><td style="text-align:right;font-variant-numeric:tabular-nums">' + esc(fmt$(cashPaid)) + '</td></tr>' : '') +
'          <tr><td colspan="3" style="text-align:right">Your side total</td><td style="text-align:right;font-variant-numeric:tabular-nums">' + esc(fmt$(yourSideTotal)) + '</td></tr>' +
'        </tfoot>\n' +
'      </table>\n' +
'    </div>\n' +
'    <div class="side them">\n' +
'      <h2>You receive</h2>\n' +
'      <table>\n' +
'        <thead><tr><th>Item</th><th class="r">Qty</th><th class="r">FMV / unit</th><th class="r">Line FMV</th></tr></thead>\n' +
'        <tbody>' + incRows + '</tbody>\n' +
'        <tfoot>' +
'          <tr><td colspan="3" style="text-align:right">Incoming FMV</td><td style="text-align:right;font-variant-numeric:tabular-nums">' + esc(fmt$(inFMV)) + '</td></tr>' +
          (cashReceived > 0 ? '<tr><td colspan="3" style="text-align:right">+ Cash you receive</td><td style="text-align:right;font-variant-numeric:tabular-nums">' + esc(fmt$(cashReceived)) + '</td></tr>' : '') +
'          <tr><td colspan="3" style="text-align:right">Their side total</td><td style="text-align:right;font-variant-numeric:tabular-nums">' + esc(fmt$(theirSideTotal)) + '</td></tr>' +
'        </tfoot>\n' +
'      </table>\n' +
'    </div>\n' +
'  </div>\n' +
'  <div class="totals">\n' +
'    <div class="row"><span>Your side total (incl. cash)</span><span style="font-variant-numeric:tabular-nums">' + esc(fmt$(yourSideTotal)) + '</span></div>\n' +
'    <div class="row"><span>Their side total (incl. cash)</span><span style="font-variant-numeric:tabular-nums">' + esc(fmt$(theirSideTotal)) + '</span></div>\n' +
    (cashLabel ? '    <div class="row"><span>' + cashLabel + '</span><span></span></div>\n' : '') +
'    <div class="row net ' + (netGL >= 0 ? 'pos' : 'neg') + '"><span>Net to you' + (netGL >= 0 ? ' (gain)' : ' (loss)') + '</span><span style="font-variant-numeric:tabular-nums">' + esc(fmt$(netGL)) + '</span></div>\n' +
'  </div>\n' +
   (trade["Notes"] ? '  <div class="notes"><b>Notes:</b><br>' + esc(trade["Notes"]) + '</div>\n' : '') +
'  <script>window.addEventListener("load", () => { setTimeout(() => window.print(), 200); });</script>\n' +
'</body></html>';

  win.document.open();
  win.document.write(html);
  win.document.close();
}
(window as any).exportTradeProposal = exportTradeProposal;
